import React, { FC, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import classNames from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IAppliedVideo, IComment } from "./constants";
import { useVideoFeedback } from "./useVideoFeedback";
import { usePortalPath } from "../../../../hooks/usePortalPath";
import { menuIcon } from "../../../../components/MenuOptions/OverflowMenu";
import { PLAY_GLYPH, thumbSource } from "../../../../components/videoSection/videoThumb";
import { groupVariantOptions } from "../../../../components/videoSection/videoDetails";
import { formatVideoName } from "../../../admin/videos/hooks/useFetchVideos";
import { IconChevronDown } from "../../../../utils/icons";

// MENU_ICONS.duplicate's path, drawn at the chip's 12px.
const DUPLICATE_GLYPH = menuIcon(
    '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M15 5.5A2.5 2.5 0 0 0 12.5 3H5.5A2.5 2.5 0 0 0 3 5.5v7A2.5 2.5 0 0 0 5.5 15"/>',
    12,
    1.8
);

// The house OverflowMenu row geometry (§05), two lines tall.
const ROW_SX = {
    minHeight: 48,
    borderRadius: "8px",
    px: "8px",
    mx: "6px",
    gap: "10px",
    fontFamily: "inherit",
    "&:hover": { background: "var(--surface-2)" },
    // The current video stays readable; it is listed for orientation only.
    "&.Mui-disabled": { opacity: 1, cursor: "default" }
};

interface Props {
    comment: IComment;
}

/**
 * "Applied to N videos" on every comment of an "Apply to other videos" group.
 * Opens a menu of the group's videos; picking one opens that video's review
 * on its copy of the comment, the same link a comment notification builds.
 */
export const AppliedVideosChip: FC<Props> = ({ comment }) => {
    const { project, video, onCloseReview } = useVideoFeedback();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { p } = usePortalPath();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const applied = comment.applied_to || [];
    if (applied.length < 2) return null;

    const projectId = project?.id || video?.projectId;
    const isCurrent = ({ file }: IAppliedVideo) =>
        file?.id === video?.id || Boolean(video?.hash && file?.hash === video.hash);
    // This video first, the rest in the API's order (channel, then file).
    const entries = [...applied.filter(isCurrent), ...applied.filter((e) => !isCurrent(e))];
    const label = `Applied to ${applied.length} videos`;
    const menuId = `applied-videos-${comment.id}`;

    const openVideo = ({ comment_id: commentId, file }: IAppliedVideo) => {
        setAnchorEl(null);
        if (!projectId || !file?.id) return;
        let link = `/video-production/projects/${projectId}?tab=downloads&review=${file.id}&comment=${commentId}`;
        // Seek offset, the same rule as NotificationControl's deep link (copies
        // keep the source's timestamps).
        if (comment.timestamp != null) link += `&t=${comment.timestamp}`;
        // A review opened from ?review= (the Downloads tab) swaps to the new
        // video in place. One opened from a card or a feed is its own modal:
        // close it first, or Downloads would stack a second review on top.
        if (searchParams.get("review") !== String(video?.id)) onCloseReview?.();
        navigate(p(link));
    };

    const renderEntry = (entry: IAppliedVideo) => {
        const { file } = entry;
        const current = isCurrent(entry);
        const title = formatVideoName(file?.name || file?.key || "") || "Untitled video";
        const subtitle = [file?.content_channel?.name, ...groupVariantOptions(file).map((o) => o.displayValue)]
            .filter(Boolean)
            .join(" · ");
        return (
            <MenuItem
                key={`${file?.id}-${entry.comment_id}`}
                disableRipple
                disabled={current}
                onClick={() => openVideo(entry)}
                sx={ROW_SX}
            >
                <span className="relative w-12 h-[27px] shrink-0 rounded-md bg-track text-muted flex items-center justify-center overflow-hidden">
                    {PLAY_GLYPH(12)}
                    <img
                        src={thumbSource(file)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                    />
                </span>
                <span className="flex-1 min-w-0 flex flex-col">
                    <span className="flex items-center gap-2 min-w-0">
                        <span className="font-bold text-text-1 truncate min-w-0" style={{ fontSize: 13, lineHeight: 1.35 }}>
                            {title}
                        </span>
                        {current && (
                            <span
                                className="inline-flex items-center h-[22px] px-[9px] rounded-full bg-tint text-accent-text whitespace-nowrap shrink-0"
                                style={{ fontSize: 11 }}
                            >
                                This video
                            </span>
                        )}
                    </span>
                    {subtitle && (
                        <span className="text-text-2 truncate" style={{ fontSize: 11.5, lineHeight: 1.35 }}>
                            {subtitle}
                        </span>
                    )}
                </span>
            </MenuItem>
        );
    };

    return (
        <>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={`${label} — show videos`}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                className={classNames(
                    "inline-flex items-center gap-1 h-[22px] pl-[7px] pr-[6px] rounded-full bg-tint text-accent-text whitespace-nowrap transition-shadow hover:ring-1 hover:ring-accent",
                    { "ring-1 ring-accent": open }
                )}
                style={{ fontSize: 11, fontWeight: 500 }}
            >
                {DUPLICATE_GLYPH}
                {label}
                <IconChevronDown size={12} open={open} />
            </button>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 320,
                            maxWidth: "calc(100vw - 32px)",
                            maxHeight: 360,
                            borderRadius: "12px",
                            border: "1px solid var(--border)",
                            boxShadow: "var(--sh-2)",
                            background: "var(--surface)",
                            backgroundImage: "none"
                        }
                    }
                }}
                MenuListProps={{ sx: { py: "6px" } }}
            >
                {entries.map(renderEntry)}
            </Menu>
        </>
    );
};
